import React, { useCallback, createContext, useContext } from "react";
import { Mercator, Graticule } from "@visx/geo";
import { RectClipPath } from "@visx/clip-path";
import { Zoom } from "@visx/zoom";
import { ProvidedZoom } from "@visx/zoom/lib/types";
import * as topojson from "topojson-client";
import { geoMercator } from "d3-geo";
import {
  useTooltip as useVisxTooltip,
  useTooltipInPortal
} from "@visx/tooltip";
import { UseTooltipParams } from "@visx/tooltip/lib/hooks/useTooltip";
import { useSpring, animated, to } from "@react-spring/web";
import { scaleOrdinal } from "@visx/scale";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import topology from "../scripts/russia-topo.json"; import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { fabrics } from "../scripts/fabrics";
;

export interface MapGeolocationInterface {
  latitude: number;
  longitude: number;
  count: number;
}

interface FeatureShape {
  type: "Feature";
  id: string;
  geometry: { coordinates: [number, number][][]; type: "Polygon" };
  properties: { name: string };
}

type TooltipData = {
  count: string;
  extras: {
    label: string;
    value: any;
  }[];
};

// @ts-ignore
const world = topojson.feature(topology, topology.objects.russia) as {
  type: "FeatureCollection";
  features: FeatureShape[];
};

const borderColor = "#6B7280";
const oceanColor = "#FFF";
const landColor = "#E5E7EB";
const bubbleColor = "#4FD1C5";

const TooltipContext = createContext({} as UseTooltipParams<TooltipData>);
export const useTooltip = (): UseTooltipParams<TooltipData> =>
  useContext(TooltipContext);

const ZoomContext = createContext({} as ProvidedZoom<Element> & any);
export const useZoom = (): ProvidedZoom<Element> & any =>
  useContext(ZoomContext);

const ConfigContext = createContext({} as any);
export const useConfig = (): any => useContext(ConfigContext);

/**
 * A map component which takes an array of coordinates (and other info) and displays them on a map of the UK.
 * @param data - The data to display on the map
 * @param keys - Which of the properties passed should appear in tooltips
 * @param tooltipString - String used in the tooltip
 */
const ChartMapIndustry = (): JSX.Element => {
  let data = fabrics
  let keys = ["Предприятие"]
  let tooltipString = "надежность"
  
  let center = [50, 67]

  const lowestCount = data?.map((el) => el.count).sort((a: any, b: any) => a - b);
  const highestCount = [...lowestCount].reverse();
  return (
    <ConfigContext.Provider
      value={{
        lowestCount: lowestCount[0],
        highestCount: highestCount[0],
        keys,
        tooltipString,
        center
      }}
    >
      <ParentSize>
        {/* eslint-disable-next-line react/no-unused-prop-types */}
        {({ width, height }: { width: number; height: number }) => (
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          <MapInternal width={width} height={height} data={data} />
        )}
      </ParentSize>
    </ConfigContext.Provider>
  );
};

// Helps scaling circle radius depending on smallest and biggest value, might require some fine tuning.
// In cases in which all numbers are 1 or 0 the result might be NaN or Infinity, so we check for it.
const scaleBetween = (
  unscaledNum: number,
  minAllowed: number,
  maxAllowed: number,
  min: number,
  max: number
) => {
  const scaledValue =
    ((maxAllowed - minAllowed) * (unscaledNum - min)) / (max - min) +
    minAllowed;
  return !Number.isNaN(scaledValue) && Number.isFinite(scaledValue)
    ? scaledValue
    : minAllowed;
};

const colors = Array(10)
  .fill(0)
  .map((n, i) => `hsl(223, 84%, ${56 + i * 5}%)`);

const MapInternal = ({
  width,
  height,
  data
}: {
  width: number;
  height: number;
  data: (MapGeolocationInterface & { [key: string]: any })[];
}) => {
  const scale = 0.5;
  const centerX = -480 * scale + width / 2;
  const centerY = -240 * scale + height / 2;

  const { containerRef, containerBounds, TooltipInPortal } = useTooltipInPortal(
    {
      scroll: true
    }
  );

  const tooltip = useVisxTooltip<TooltipData>({
    tooltipOpen: false
  });

  const { keys, tooltipString } = useConfig();

  const colorScale = scaleOrdinal<string, string>({
    domain: keys,
    range: colors
  });

  return width < 10 ? null : (
    <Zoom
      width={width}
      height={height}
      scaleXMin={0.5}
      scaleXMax={5}
      scaleYMin={0.5}
      scaleYMax={5}
      initialTransformMatrix={{
        scaleX: scale,
        scaleY: scale,
        translateX: centerX,
        translateY: centerY,
        skewX: 0,
        skewY: 0
      }}
    >
      {(zoom) => (
        <div ref={containerRef} className="white-block-map">
          <ZoomContext.Provider value={zoom}>
            <TooltipContext.Provider value={tooltip}>
              <div className="zoom">
                <PlusOutlined onClick={() => zoom.scale({ scaleX: 1.2, scaleY: 1.2 })} />
                <MinusOutlined onClick={() => zoom.scale({ scaleX: 0.8, scaleY: 0.8 })} />
                {/*<button
                  type="button"
                  className="btn btn-lg"
                  onClick={zoom.reset}
                >
                  Reset
                </button>*/}
              </div>
              {tooltip.tooltipOpen && tooltip.tooltipData && (
                <TooltipInPortal
                  top={tooltip.tooltipTop! - containerBounds.top}
                  left={tooltip.tooltipLeft! - containerBounds.left}
                  style={{
                    backgroundColor: "hsl(0deg 0% 0% / 75%)",
                    padding: "0.25rem 0.75rem",
                    color: "white",
                    position: "absolute",
                    borderRadius: "0.25rem",
                    fontFamily: "sans-serif"
                  }}
                >
                  {tooltip.tooltipData.extras.map((extra, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i}>
                      <div
                        style={{
                          color: colorScale(extra.label),
                          textTransform: "capitalize"
                        }}
                      >
                        <strong>{extra.label}</strong>
                      </div>
                      <div>{extra.value}</div>
                    </div>
                  ))}
                  <div style={{ fontSize: ".875rem" }}>
                    {(+tooltip.tooltipData.count)*12} {tooltipString}
                  </div>
                </TooltipInPortal>
              )}
              {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
              <Graph width={1100} height={400} data={data} />
              <style>
                {`
                    .btn {
                      margin: 0;
                      text-align: center;
                      border: none;
                      background: #2f2f2f;
                      padding: 0 4px;
                      border-top: 1px solid #0a0a0a;
                    }
                    .btn-lg {
                      font-size: 12px;
                      line-height: 1;
                      padding: 4px;
                    }
                    .btn-zoom {
                      width: 26px;
                      font-size: 22px;
                    }
                    .btn-bottom {
                      margin-bottom: 1rem;
                    }
                    .controls {
                      position: absolute;
                      top: 15px;
                      right: 15px;
                      display: flex;
                      flex-direction: column;
                      align-items: flex-end;
                      z-index: 100
                    }
                  `}
              </style>
            </TooltipContext.Provider>
          </ZoomContext.Provider>
        </div>
      )}
    </Zoom>
  );
};

// memoize so no re-render occurs even if parent re-renders
const MercatorProjection = React.memo(
  ({
    data,
    isDragging
  }: {
    data: (MapGeolocationInterface & { [key: string]: any })[];
    isDragging: boolean;
  }) => {
    const scale = 300;

    const { showTooltip, hideTooltip } = useTooltip();

    // event handlers
    const handlePointerMove = useCallback(
      (event: React.PointerEvent<SVGCircleElement>, tooltipData: any) => {
        // coordinates should be relative to the container in which Tooltip is rendered
        const containerX = "clientX" in event ? event.clientX : 0;
        const containerY = "clientY" in event ? event.clientY : 0;

        showTooltip({
          tooltipLeft: containerX,
          tooltipTop: containerY,
          tooltipData
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [showTooltip]
    );

    const { lowestCount, highestCount, keys, center } = useConfig();
    const projection = geoMercator().center(center).scale(scale); // scale is off?

    return (
      <>
        <Mercator<FeatureShape>
          data={world.features}
          scale={scale}
          center={center}
        >
          {(mercator) => (
            <g>
              <Graticule
                graticule={(g) => mercator.path(g) || ""}
                stroke="rgba(33,33,33,0.05)"
              />
              {mercator.features.map(({ path }, i) => (
                <React.Fragment key={i}>
                  <path
                    key={`map-feature-${i}`}
                    d={path || ""}
                    fill={landColor}
                    stroke={borderColor}
                    strokeWidth={0.5}
                  />
                </React.Fragment>
              ))}
            </g>
          )}
        </Mercator>
        <g>
          {data.map((entry, i) => {
            const extras = keys.map((key: string) => ({
              value: entry[key],
              label: key
            }));
            return (
              <circle
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                r={scaleBetween(entry.count, 2, 7, lowestCount, highestCount)}
                x={entry.longitude}
                y={entry.latitude}
                fill={bubbleColor}
                stroke="#333"
                className="map__circle hover:cursor-pointer opacity-50 hover:opacity-70 transition-opacity"
                strokeWidth={0}
                onPointerOver={(e) => {
                  if (!isDragging)
                    handlePointerMove(e, { count: entry.count, extras });
                }}
                onPointerLeave={() => {
                  if (!isDragging) hideTooltip();
                }}
                opacity={0.7}
                transform={`translate(${projection([
                  entry.longitude,
                  entry.latitude
                ])})`}
              />
            );
          })}
        </g>
      </>
    );
  }
);

const Graph = ({
  width,
  height,
  data
}: {
  width: number;
  height: number;
  data: (MapGeolocationInterface & { [key: string]: any })[];
}) => {
  const zoom = useZoom();

  const { x, y, scaleY } = useSpring({
    x: zoom.transformMatrix.translateX,
    y: zoom.transformMatrix.translateY,
    scaleY: zoom.transformMatrix.scaleY
  });

  return (
      <svg
        width={width}
        height={height}
        className="map-source"
        onMouseDown={zoom.dragStart}
        onMouseMove={zoom.dragMove}
        onMouseUp={zoom.dragEnd}
        onMouseLeave={() => {
          if (zoom.isDragging) zoom.dragEnd();
        }}
        style={{
          cursor: zoom.isDragging ? "grabbing" : "grab",
          touchAction: "none"
        }}
        ref={zoom.containerRef}
      >
        <RectClipPath id="zoom-clip" width={width} height={height} />
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={oceanColor}
          rx={14}
        />

        {/** apply the tranform to the g so we don't have to re-render the entire map */}
        <animated.g
          transform={to(
            [x, y, scaleY],
            (tx, ty, tscaleY) =>
              `translate(${tx}, ${ty}) scale(${tscaleY}, ${tscaleY})`
          )}
        >
          <MercatorProjection data={data} isDragging={zoom.isDragging} />
        </animated.g>
      </svg>
  );
};

export default ChartMapIndustry;
